import { Clock, CalendarDays, Plane, Zap, X, AlertTriangle } from "lucide-react";
import { Button } from "./ui/button";
import { fmtH } from "../lib/planning";

const FLAG_LABELS = {
    uscita_mancante: "Uscita mancante", entrata_mancante: "Entrata mancante",
    fuori_assegnazione: "Fuori assegnazione", in_corso: "In corso",
};

const fmtLong = (iso) => new Date(`${iso}T00:00:00`).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

const Section = ({ icon: Icon, title, tone, count, children, testId }) => (
    <div className="border-b border-slate-100 py-3 last:border-0" data-testid={testId}>
        <div className={`mb-1.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide ${tone}`}>
            <Icon className="h-3.5 w-3.5" /> {title} <span className="font-mono-numbers font-semibold text-slate-400">({count})</span>
        </div>
        {count === 0 ? <div className="text-xs text-slate-400">Nessuna voce</div> : <div className="space-y-1">{children}</div>}
    </div>
);

const Row = ({ name, matricola, children, testId }) => (
    <div className="rounded border border-slate-100 bg-slate-50/60 px-2 py-1.5 text-xs" data-testid={testId}>
        <div className="font-semibold text-slate-700">{name} {matricola && <span className="font-normal text-slate-400">· matr. {matricola}</span>}</div>
        <div className="text-slate-600">{children}</div>
    </div>
);

export const CalendarDayDetail = ({ day, onClose }) => {
    if (!day) return null;
    return (
        <aside className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid="cal-detail-panel">
            <div className="flex items-start justify-between gap-2 border-b border-slate-100 px-4 py-3">
                <div>
                    <div className="font-display text-sm font-bold capitalize text-slate-800" data-testid="cal-detail-date">{fmtLong(day.date)}</div>
                    <div className="text-[11px] text-slate-500">
                        {fmtH(day.totals.ore_timbrate)}h timbrate · {day.totals.programmati} turni · {day.totals.assenze} assenze · {fmtH(day.totals.ore_straordinari)}h straord.
                    </div>
                </div>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose} data-testid="cal-detail-close"><X className="h-4 w-4" /></Button>
            </div>
            <div className="max-h-[70vh] overflow-y-auto px-4">
                <Section icon={Clock} title="Timbrature" tone="text-emerald-700" count={day.timbrature.length} testId="cal-detail-timbrature">
                    {day.timbrature.map((t) => (
                        <Row key={t.employee_id} name={t.employee_name} matricola={t.matricola} testId={`cal-detail-timb-${t.employee_id}`}>
                            <span className="font-mono-numbers">{t.stamps.map((s) => `${s.type === "in" ? "E" : "U"} ${s.time}${s.manual ? "*" : ""}`).join(" · ")}</span>
                            <span className="ml-2 font-semibold text-emerald-700">{fmtH(t.ore)}h</span>
                            <div className="text-[11px] text-slate-500">{t.worksites.join(", ")}</div>
                            {t.flags.filter((f) => f !== "in_corso").length > 0 && (
                                <div className="mt-0.5 flex items-center gap-1 text-[11px] font-semibold text-red-600">
                                    <AlertTriangle className="h-3 w-3" /> {t.flags.filter((f) => f !== "in_corso").map((f) => FLAG_LABELS[f] || f).join(", ")}
                                </div>
                            )}
                            {t.flags.includes("in_corso") && <div className="text-[11px] font-semibold text-sky-600">In corso</div>}
                        </Row>
                    ))}
                </Section>
                <Section icon={CalendarDays} title="Turni programmati" tone="text-[#376DB3]" count={day.programmati.length} testId="cal-detail-programmati">
                    {day.programmati.map((p, i) => (
                        <Row key={`${p.employee_id}-${i}`} name={p.employee_name} matricola={p.matricola} testId={`cal-detail-prog-${p.employee_id}-${i}`}>
                            <span className={`font-mono-numbers ${p.status === "assente" ? "line-through text-red-500" : ""}`}>{p.start}–{p.end}</span>
                            <span className="ml-1">{p.worksite_name}{p.client_name ? ` — ${p.client_name}` : ""} · {p.activity_name} · {fmtH(p.hours)}h</span>
                            {p.status === "assente" && (
                                <div className="text-[11px] font-semibold text-red-600">
                                    Assente{p.reason ? ` (${p.reason})` : ""}{p.sub_by_name ? ` · sostituito da ${p.sub_by_name}` : " · turno scoperto"}
                                </div>
                            )}
                            {p.status === "sostituto" && (
                                <div className="text-[11px] font-semibold text-sky-700">Sostituisce {p.sub_for_name}{p.reason ? ` (${p.reason})` : ""}</div>
                            )}
                        </Row>
                    ))}
                </Section>
                <Section icon={Plane} title="Assenze" tone="text-red-600" count={day.assenze.length} testId="cal-detail-assenze">
                    {day.assenze.map((a, i) => (
                        <Row key={`${a.employee_id}-${i}`} name={a.employee_name} matricola={a.matricola} testId={`cal-detail-abs-${a.employee_id}-${i}`}>
                            <span className="font-semibold text-red-600">{a.label}</span>
                            {a.time_from && a.time_to && <span className="ml-1 font-mono-numbers">{a.time_from}–{a.time_to}</span>}
                            {a.date_from && a.date_from !== a.date_to && <span className="ml-1 text-slate-500">(dal {a.date_from.slice(8)}/{a.date_from.slice(5, 7)} al {a.date_to.slice(8)}/{a.date_to.slice(5, 7)})</span>}
                            {a.sub_by_name && <div className="text-[11px] text-slate-500">Sostituito da {a.sub_by_name}</div>}
                            {a.note && <div className="text-[11px] italic text-slate-500">{a.note}</div>}
                        </Row>
                    ))}
                </Section>
                <Section icon={Zap} title="Straordinari" tone="text-violet-700" count={day.straordinari.length} testId="cal-detail-straordinari">
                    {day.straordinari.map((x) => (
                        <Row key={x.id} name={x.employee_name} matricola={x.matricola} testId={`cal-detail-extra-${x.id}`}>
                            <span className="font-semibold text-violet-700">{fmtH(x.hours)}h</span>
                            {x.time_from && x.time_to && <span className="ml-1 font-mono-numbers">{x.time_from}–{x.time_to}</span>}
                            <span className="ml-1">{x.description}</span>
                            <div className="text-[11px] text-slate-500">{x.worksite_name || "Senza cantiere"} · {x.status === "confermata" ? "confermato" : x.status === "assegnata" ? "assegnato dall'ufficio" : "da confermare"}</div>
                        </Row>
                    ))}
                </Section>
            </div>
        </aside>
    );
};
